import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { HazardMeter } from "./Badges";

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "environment", label: "Environment" },
  { key: "lore", label: "Lore" },
  { key: "life", label: "Notable Life" },
  { key: "travel", label: "Travel Notes" },
];

const Spec = ({ label, value }) => (
  <div className="glass rounded-lg p-4" data-testid={`spec-${label.toLowerCase().replace(/\s/g, "-")}`}>
    <p className="label-mono mb-1">{label}</p>
    <p className="font-mono text-sm text-slate-100">{value}</p>
  </div>
);

const List = ({ items, testid }) => (
  <ul className="grid sm:grid-cols-2 gap-3" data-testid={testid}>
    {items.map((it, i) => (
      <li key={i} className="flex items-start gap-3 text-sm text-slate-300">
        <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-cyan-400 shrink-0 shadow-[0_0_8px_#00F0FF]" />{it}
      </li>
    ))}
  </ul>
);

export const PlanetTabs = ({ planet }) => {
  const [tab, setTab] = useState("overview");

  const panels = {
    overview: (
      <div className="space-y-8">
        <p className="text-base leading-relaxed text-slate-300 max-w-3xl">{planet.overview}</p>
        <div>
          <p className="label-mono mb-3">Known hazards</p>
          <HazardMeter level={planet.hazard_level} />
          <div className="mt-4"><List items={planet.hazards} testid="hazards-list" /></div>
        </div>
      </div>
    ),
    environment: (
      <div className="space-y-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Spec label="Atmosphere" value={planet.atmosphere} />
          <Spec label="Gravity" value={planet.gravity} />
          <Spec label="Temperature" value={planet.temperature} />
          <Spec label="Day Length" value={planet.day_length} />
        </div>
        <p className="text-base leading-relaxed text-slate-300 max-w-3xl">{planet.environment}</p>
        <div>
          <p className="label-mono mb-3">Landmarks & phenomena</p>
          <List items={planet.environment_features} testid="environment-features" />
        </div>
      </div>
    ),
    lore: (
      <div className="space-y-5 max-w-3xl">
        <p className="label-mono">Tone · {planet.lore_tone}</p>
        <p className="text-base sm:text-lg leading-relaxed text-slate-200 first-letter:font-display first-letter:text-4xl first-letter:text-cyan-300 first-letter:mr-1 first-letter:float-left">
          {planet.lore}
        </p>
      </div>
    ),
    life: (
      <div className="grid md:grid-cols-3 gap-4" data-testid="notable-life-list">
        {planet.notable_life.map((lf, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}
            className="glass hud-corners rounded-lg p-5">
            <p className="font-mono text-[0.62rem] tracking-[0.25em] text-[#00FF66] uppercase mb-2">Specimen 0{i + 1}</p>
            <h4 className="font-display text-base font-semibold text-slate-50 mb-2">{lf.name}</h4>
            <p className="text-sm text-slate-300 leading-relaxed">{lf.description}</p>
          </motion.div>
        ))}
      </div>
    ),
    travel: (
      <div className="space-y-8">
        <p className="text-base leading-relaxed text-slate-300 max-w-3xl">{planet.travel_notes}</p>
        <div>
          <p className="label-mono mb-3">Field tips</p>
          <List items={planet.travel_tips} testid="travel-tips" />
        </div>
      </div>
    ),
  };

  return (
    <section className="glass rounded-xl overflow-hidden" data-testid="planet-tabs">
      <div className="flex overflow-x-auto border-b border-cyan-500/15 px-2">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} className={`tab-btn whitespace-nowrap ${tab === t.key ? "active" : ""}`} data-testid={`tab-${t.key}`}>
            {t.label}
          </button>
        ))}
      </div>
      <div className="p-6 sm:p-8 min-h-[16rem]">
        <AnimatePresence mode="wait">
          <motion.div key={tab} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.22 }}
            data-testid={`tab-panel-${tab}`}>
            {panels[tab]}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};
